/**
 * Pure previous/next series-navigation logic for the guide and playbook
 * detail pages, built from the published rows of a single `seriesFamily`.
 *
 * A series crosses shelves — the Proof series spans Building, Starting Out
 * and Scaling, the Stage ladder spans four shelves — so it cannot render as
 * a rail like `library-shelves.ts` does. It surfaces instead as
 * previous/next navigation on the detail page itself.
 *
 * The one rule this module exists to enforce: the stored `seriesPosition`
 * never reaches a reader. The sheet's position column decides *order* only —
 * the displayed step number and the series total are both derived from the
 * count and order of the *published* rows the caller hands in, so a member
 * being unpublished can never produce a claim ("5 of 4") a reader can't
 * click through to verify. Positions may have gaps (an unpublished sibling);
 * the displayed position is the row's index in the published, ordered list,
 * never its stored `seriesPosition`.
 *
 * No DB import, no hardcoded slugs or titles, no per-row URL — mirrors
 * `library-shelves.ts`'s `ShelvableGuide`/`ShelvablePlaybook` input style so
 * callers pass their own richer row types through untouched, and the two
 * route templates derive a neighbour's URL from its `type` discriminant via
 * `~/components/cih/library-link`.
 *
 * Aliases: series navigation, previous next series, series nav, reading
 * sequence nav, series siblings
 */

import type { SeriesFamily } from "~/lib/shared/content-status";

/** The minimal shape this module reads off a guide. */
export interface SeriesGuide {
  readonly slug: string;
  readonly title: string;
  readonly seriesPosition: number | null;
}

/** The minimal shape this module reads off a playbook. */
export interface SeriesPlaybook {
  readonly slug: string;
  readonly title: string;
  readonly seriesPosition: number | null;
}

/**
 * A series member tagged with its content type and carrying the caller's own
 * (possibly much richer) guide/playbook object, so a template that needs
 * `description` or a hero image for a neighbour card gets it back without
 * this module needing to know the field exists. Mirrors `LibraryEntry` in
 * `library-shelves.ts`.
 */
export type SeriesEntry<
  TGuide extends SeriesGuide,
  TPlaybook extends SeriesPlaybook,
> =
  | { readonly type: "guide"; readonly item: TGuide }
  | { readonly type: "playbook"; readonly item: TPlaybook };

/** Identifies which of the passed-in rows is the page currently rendering. */
export interface CurrentSeriesPage {
  readonly type: "guide" | "playbook";
  readonly slug: string;
}

export interface SeriesNavResult<
  TGuide extends SeriesGuide,
  TPlaybook extends SeriesPlaybook,
> {
  readonly family: SeriesFamily;
  /**
   * 1-based position of the current page among published members — its
   * index in the ordered list below, never the stored `seriesPosition`.
   */
  readonly position: number;
  /** Count of published members — always equal to what a reader can click through. */
  readonly total: number;
  readonly previous: SeriesEntry<TGuide, TPlaybook> | null;
  readonly next: SeriesEntry<TGuide, TPlaybook> | null;
}

/**
 * Order two same-family members: `seriesPosition` ascending, nulls last, ties
 * (including null vs. null) broken by title. Mirrors
 * `compareByCurriculumOrder` in `library-shelves.ts`.
 */
function compareBySeriesPosition(
  a: { readonly seriesPosition: number | null; readonly title: string },
  b: { readonly seriesPosition: number | null; readonly title: string },
): number {
  if (a.seriesPosition !== null && b.seriesPosition !== null) {
    return (
      a.seriesPosition - b.seriesPosition || a.title.localeCompare(b.title)
    );
  }
  if (a.seriesPosition !== null) return -1;
  if (b.seriesPosition !== null) return 1;
  return a.title.localeCompare(b.title);
}

/**
 * Build the previous/next series nav for the page identified by `current`.
 *
 * `guides`/`playbooks` must already be filtered to published rows of a single
 * `family` (see `getPublishedSeriesMembers` in
 * `~/lib/server/cih/series`) — this module trusts that filtering and does no
 * status or family checks of its own.
 *
 * Returns `null` when `current` isn't found among the passed-in rows (the
 * page carries no series the caller resolved) or when it is the family's
 * only published member — either way there is nothing to navigate to.
 */
export function buildSeriesNav<
  TGuide extends SeriesGuide,
  TPlaybook extends SeriesPlaybook,
>(
  family: SeriesFamily,
  guides: readonly TGuide[],
  playbooks: readonly TPlaybook[],
  current: CurrentSeriesPage,
): SeriesNavResult<TGuide, TPlaybook> | null {
  const members: SeriesEntry<TGuide, TPlaybook>[] = [
    ...guides.map((item): SeriesEntry<TGuide, TPlaybook> => ({
      type: "guide",
      item,
    })),
    ...playbooks.map((item): SeriesEntry<TGuide, TPlaybook> => ({
      type: "playbook",
      item,
    })),
  ].sort((a, b) => compareBySeriesPosition(a.item, b.item));

  if (members.length <= 1) return null;

  const index = members.findIndex(
    (entry) => entry.type === current.type && entry.item.slug === current.slug,
  );
  if (index === -1) return null;

  return {
    family,
    position: index + 1,
    total: members.length,
    previous: index > 0 ? members[index - 1] : null,
    next: index < members.length - 1 ? members[index + 1] : null,
  };
}
